import React, { useState, useCallback, useEffect } from "react";
import { Check, X } from "react-feather";
import { useApi } from "../hooks/useApi";
import InviteLinkBox from "./InviteLinkBox";

const approvalStatusStyles = {
  Pending: "bg-yellow-100 text-yellow-800",
  Approved: "bg-green-100 text-green-800",
  Rejected: "bg-red-100 text-red-800",
};

// Pending signups sort to the top so they surface for review; Rejected sink to the bottom.
const approvalSortOrder = (status) =>
  status === "Pending" ? 0 : status === "Rejected" ? 2 : 1;

// MSSP signups create an account + tenant but no organization, so they never show in the
// organizations table. Their approval gate lives on the tenant. Rendered only inside the
// SuperAdmin page, which already redirects anyone who is not a super admin; the endpoints are
// super-admin-only on the backend as well.
const MsspSignupApprovals = () => {
  const apiCall = useApi();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actioningTenantId, setActioningTenantId] = useState(null);
  // Owner invite link from an approval while email is off: { id, url }. Held only in component
  // state (it is a credential). Shown after the approval's own reload; the next reload clears it.
  const [ownerLink, setOwnerLink] = useState(null);

  const loadTenants = useCallback(async () => {
    setOwnerLink(null);
    const data = await apiCall("GET", "/SuperAdmin/mssp-tenants");
    setTenants(
      (data || []).sort(
        (a, b) => approvalSortOrder(a.approvalStatus) - approvalSortOrder(b.approvalStatus)
      )
    );
  }, [apiCall]);

  useEffect(() => {
    const fetchTenants = async () => {
      try {
        setLoading(true);
        await loadTenants();
      } catch (err) {
        setError("Failed to load MSSP signups");
      } finally {
        setLoading(false);
      }
    };
    fetchTenants();
  }, [loadTenants]);

  const runAction = async (tenant, action, failureMessage) => {
    setActioningTenantId(tenant.tenantId);
    setError(null);
    try {
      const res = await apiCall("POST", `/SuperAdmin/tenants/${tenant.tenantId}/${action}`, null);
      await loadTenants();
      if (action === "approve" && res?.ownerInviteUrl) {
        setOwnerLink({ id: tenant.tenantId, url: res.ownerInviteUrl });
      }
    } catch (err) {
      setError(err.response?.data?.message || failureMessage);
    } finally {
      setActioningTenantId(null);
    }
  };

  return (
    <div className="mt-10">
      <h2 className="text-lg font-semibold text-content-primary mb-1">MSSP signups</h2>
      <p className="text-sm text-content-secondary mb-4">
        MSSP accounts have no organization, so they are approved here. The owner's invite email is
        sent on approval.
      </p>

      {error && (
        <div className="mb-4 p-3 bg-risk-critical/10 border border-risk-critical/30 rounded-lg text-risk-critical text-sm">
          {error}
        </div>
      )}

      <div className="bg-theme-secondary border border-border rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border bg-theme-primary/50">
              {["MSSP", "Tenant ID", "Domain", "Signed up", "Status", "Actions"].map((h) => (
                <th
                  key={h}
                  className="text-left px-6 py-3 text-sm font-medium text-content-secondary"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tenants.map((tenant) => (
              <React.Fragment key={tenant.tenantId}>
              <tr
                className="border-b border-border last:border-b-0 hover:bg-theme-hover transition-colors"
              >
                <td className="px-6 py-4 text-sm text-content-primary font-medium">
                  {tenant.tenantName}
                </td>
                <td className="px-6 py-4 text-sm text-content-secondary">{tenant.tenantId}</td>
                <td className="px-6 py-4 text-sm text-content-secondary">
                  {tenant.domain || "—"}
                </td>
                <td className="px-6 py-4 text-sm text-content-secondary">
                  {tenant.createdAt ? new Date(tenant.createdAt).toLocaleDateString() : "—"}
                </td>
                <td className="px-6 py-4">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      approvalStatusStyles[tenant.approvalStatus] || "bg-gray-100 text-gray-800"
                    }`}
                    title={tenant.approvalNote || undefined}
                  >
                    {tenant.approvalStatus || "Approved"}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    {(tenant.approvalStatus === "Pending" ||
                      tenant.approvalStatus === "Rejected") && (
                      <button
                        onClick={() => runAction(tenant, "approve", "Failed to approve MSSP signup")}
                        disabled={actioningTenantId === tenant.tenantId}
                        className="flex items-center gap-1 px-2.5 py-1 text-xs text-white bg-green-600 rounded-lg hover:bg-green-700 transition-all disabled:opacity-50"
                        title="Approve and send the invite email"
                      >
                        <Check className="h-3.5 w-3.5" />
                        Approve
                      </button>
                    )}
                    {tenant.approvalStatus === "Pending" && (
                      <button
                        onClick={() => runAction(tenant, "reject", "Failed to reject MSSP signup")}
                        disabled={actioningTenantId === tenant.tenantId}
                        className="flex items-center gap-1 px-2.5 py-1 text-xs text-risk-critical border border-risk-critical/40 rounded-lg hover:bg-risk-critical/10 transition-all disabled:opacity-50"
                        title="Reject (no email sent)"
                      >
                        <X className="h-3.5 w-3.5" />
                        Reject
                      </button>
                    )}
                  </div>
                </td>
              </tr>
              {ownerLink?.id === tenant.tenantId && (
                <tr className="border-b border-border last:border-b-0">
                  <td colSpan={6} className="px-6 py-4">
                    <div className="flex items-start gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-content-secondary">
                          Email is off on this install. Send this link to the organization owner; they must sign in with the email address they signed up with.
                        </p>
                        <InviteLinkBox url={ownerLink.url} compact />
                      </div>
                      <button
                        type="button"
                        onClick={() => setOwnerLink(null)}
                        className="p-1 text-content-muted hover:text-content-primary rounded transition-colors"
                        aria-label="Dismiss invite link"
                        title="Dismiss"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              )}
              </React.Fragment>
            ))}
            {!loading && tenants.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-content-muted">
                  No MSSP signups
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MsspSignupApprovals;
